import gql from 'graphql-tag';
import { toastr } from 'react-redux-toastr';
import { reset } from 'redux-form';
import { setLoaderStart, setLoaderComplete } from '../../../actions/loader/loader';

// Runs through the Apollo client so refetchQueries can refresh the watched queries
function graphqlMutation(mutation, variables, refetchQueries) {
  return async (dispatch, getState, { client }) => {
    const { data } = await client.mutate({ mutation, variables, refetchQueries });
    return data;
  };
}

async function submit(values, dispatch) {
  const { title, message, scheduledAt, to, sendMode, imageUrl, isPersonalizedNotification, locationIds } = values;

  dispatch(setLoaderStart('sendNotification'));

  try {
    const userType = to === 'all' || !to ? null : Number(to);
    const targetLocationIds = locationIds && Array.isArray(locationIds) ? locationIds.map(id => Number(id)) : [];

    if (sendMode === 'later' && scheduledAt) {
      const utcDate = new Date(scheduledAt).toISOString();
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const data = await dispatch(graphqlMutation(
        gql`mutation createScheduledNotification(
          $title: String, $body: String, $scheduledAt: String,
          $timezone: String, $userType: Int, $imageUrl: String,
          $isPersonalizedNotification: Boolean, $locationIds: [Int]
        ) {
          createScheduledNotification(
            title: $title, body: $body, scheduledAt: $scheduledAt,
            timezone: $timezone, userType: $userType, imageUrl: $imageUrl,
            isPersonalizedNotification: $isPersonalizedNotification,
            locationIds: $locationIds
          ) { status errorMessage }
        }`,
        {
          title,
          body: message,
          scheduledAt: utcDate,
          timezone,
          userType,
          imageUrl: imageUrl || null,
          isPersonalizedNotification: isPersonalizedNotification || false,
          locationIds: targetLocationIds,
        },
        ['adminGetScheduledNotification'],
      ));

      if (data?.createScheduledNotification?.status === 200) {
        toastr.success('Scheduled!', 'The notification has been scheduled successfully.');
      } else {
        toastr.error('Error', data?.createScheduledNotification?.errorMessage || 'Failed to schedule notification.');
        return;
      }
    } else if (isPersonalizedNotification) {
      // Personalized immediate send — deliver per-token with name interpolation
      const data = await dispatch(graphqlMutation(
        gql`mutation sendPersonalizedBroadcastNotification(
          $title: String, $body: String, $imageUrl: String,
          $userType: Int, $isPersonalizedNotification: Boolean,
          $locationIds: [Int]
        ) {
          sendPersonalizedBroadcastNotification(
            title: $title, body: $body, imageUrl: $imageUrl,
            userType: $userType, isPersonalizedNotification: $isPersonalizedNotification,
            locationIds: $locationIds
          ) { status errorMessage }
        }`,
        {
          title,
          body: message,
          imageUrl: imageUrl || null,
          userType,
          isPersonalizedNotification: true,
          locationIds: targetLocationIds,
        },
      ));

      if (data?.sendPersonalizedBroadcastNotification?.status === 200) {
        toastr.success('Sent!', 'The personalized notification has been sent successfully.');
      } else {
        toastr.error('Error', data?.sendPersonalizedBroadcastNotification?.errorMessage || 'Failed to send personalized notification.');
        return;
      }
    } else {
      // Non-personalized immediate send — topic-based broadcast (existing behavior)
      const data = await dispatch(graphqlMutation(
        gql`mutation sendBroadcastNotification(
          $title: String, $body: String, $imageUrl: String,
          $userType: Int, $locationIds: [Int]
        ) {
          sendBroadcastNotification(
            title: $title, body: $body, imageUrl: $imageUrl,
            userType: $userType, locationIds: $locationIds
          ) {
            status errorMessage
          }
        }`,
        { 
          title, 
          body: message, 
          imageUrl: imageUrl || null,
          userType,
          locationIds: targetLocationIds,
        },
      ));

      if (data?.sendBroadcastNotification?.status === 200) {
        toastr.success('Sent!', 'The notification has been sent successfully.');
      } else {
        toastr.error('Error', data?.sendBroadcastNotification?.errorMessage || 'Failed to send notification.');
        return;
      }
    }

    dispatch(reset('ManageNotificationsForm'));
  } catch (error) {
    toastr.error('Error', error.message || 'Something went wrong.');
  } finally {
    dispatch(setLoaderComplete('sendNotification'));
  }
}

export default submit;
